import React from "react";
import { useTranslation } from "react-i18next";
import { analyzeStatementXmlSegments } from "../components/statement-nodes/parse";
import { segmentsToSentenceRows } from "../components/statement-nodes/rows";
import { GHS_PICTOGRAMS } from "../constants/ghsPictograms";
import type { ReferenceIssue } from "./types";

export function StatementRead({
  text,
  source,
  language,
  issues = [],
}: {
  text: string;
  source?: string;
  language: string;
  issues?: ReferenceIssue[];
}) {
  const { t } = useTranslation("work");
  const parsed = analyzeStatementXmlSegments(text);
  const rows = segmentsToSentenceRows(parsed.segments);
  const references = source
    ? segmentsToSentenceRows(analyzeStatementXmlSegments(source).segments)
    : [];
  return (
    <div className="statement-read" lang={language}>
      {rows.map((row, i) => (
        <div className="statement-read-row" key={row.rowId}>
          <div className="statement-code">
            {row.code && <strong>{row.code}</strong>}
            <span>
              {t(
                ["unresolved", "ambiguous"].includes(
                  row.attributes.matchStatus || "",
                )
                  ? "status.needs_attention"
                  : row.nodeType === "GlossaryStatementNode"
                    ? "official"
                    : "custom",
              )}
            </span>
            {row.isEdited && <span>{t("manualChanges")}</span>}
          </div>
          <div className="statement-wording">
            <p>{row.text}</p>
            {issues
              .filter(
                (issue) =>
                  (issue.row_index !== undefined
                    ? issue.row_index === i
                    : (issue.node_index ?? issue.index) === i + 1) ||
                  (!!issue.code && issue.code === row.code),
              )
              .map((issue, j) => (
                <p className="inline-warning" key={j}>
                  {issue.detail || t("referenceConflict")}
                </p>
              ))}
          </div>
          {source && (
            <p className="statement-source">
              {references.find((r) => r.id && r.id === row.id)?.text ||
                (row.code
                  ? references.find(
                      (r) =>
                        r.code === row.code &&
                        r.groupId === row.groupId &&
                        r.variantId === row.variantId,
                    )?.text
                  : undefined) ||
                "—"}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
export function Pictograms({
  selected,
  onChange,
  disabled = false,
}: {
  selected: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (!onChange)
    return (
      <div className="pictogram-row">
        {GHS_PICTOGRAMS.filter((item) => selected.includes(item.code)).map(
          (item) => (
            <img
              key={item.code}
              width="44"
              height="44"
              src={item.svgUrl}
              alt={t(item.labelKey)}
            />
          ),
        )}
      </div>
    );
  return (
    <div className="pictogram-grid">
      {GHS_PICTOGRAMS.map((item) => {
        const checked = selected.includes(item.code);
        return (
          <label
            key={item.code}
            className="pictogram-choice"
            title={t(item.labelKey)}
            data-selected={checked || undefined}
          >
            <input
              type="checkbox"
              className="sr-only"
              disabled={disabled}
              checked={checked}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...selected, item.code]
                    : selected.filter((x) => x !== item.code),
                )
              }
            />
            <img src={item.svgUrl} alt={t(item.labelKey)} />
            <span>{item.code}</span>
          </label>
        );
      })}
    </div>
  );
}
